import { Pause, Play } from 'lucide-react';
import {
  type ComponentType,
  type FocusEvent,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { cn } from '@/design-system/lib/cn';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import MembershipSlide from './MembershipSlide';
import ScheduleSlide from './ScheduleSlide';
import VisitorsSlide from './VisitorsSlide';

/** Quanto cada destaque fica na tela antes de passar para o próximo. */
const SLIDE_MS = 8000;

/** Os destaques, na ordem; o brilho do fundo acompanha cada um (classes literais para o Tailwind). */
const SLIDES: { key: string; label: string; glow: string; Slide: ComponentType<{ active: boolean }> }[] = [
  {
    key: 'escalas',
    label: 'Escalas',
    glow: 'bg-[radial-gradient(closest-side,var(--primary-hover-line),transparent)] opacity-60',
    Slide: ScheduleSlide,
  },
  {
    key: 'membresia',
    label: 'Membresia',
    glow: 'bg-[radial-gradient(closest-side,var(--accent-sky),transparent)] opacity-55',
    Slide: MembershipSlide,
  },
  {
    key: 'visitantes',
    label: 'Visitantes',
    glow: 'bg-[radial-gradient(closest-side,var(--accent-pink),transparent)] opacity-40',
    Slide: VisitorsSlide,
  },
];

function subscribeVisibility(onChange: () => void) {
  document.addEventListener('visibilitychange', onChange);

  return () => document.removeEventListener('visibilitychange', onChange);
}

/**
 * Metade direita do login (só em telas largas): o que o Live Gospel faz, em destaques que
 * se alternam — Escalas, Membresia e Visitantes. Fica clara nos dois temas (`scheme-light`),
 * como no protótipo.
 *
 * É um carrossel acessível (o padrão da WAI-ARIA): o texto de cada destaque é lido, os
 * desenhos não; a troca automática para com o ponteiro ou o foco em cima, com a aba
 * escondida e no botão Pausar, e nem começa com `prefers-reduced-motion`. A barra de cada
 * destaque mostra o tempo que falta, desenhada por `requestAnimationFrame` direto no
 * estilo (sem redesenhar o React a cada quadro).
 */
export default function Showcase() {
  const [index, setIndex] = useState(0);
  const [stopped, setStopped] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const hidden = useSyncExternalStore(
    subscribeVisibility,
    () => document.hidden,
    () => false
  );
  const running = !stopped && !hovered && !focused && !hidden && !reduced;

  const bar = useRef<HTMLSpanElement>(null);
  const elapsed = useRef(0);

  // Trocou de destaque (sozinho ou pelo clique): o tempo recomeça.
  useEffect(() => {
    elapsed.current = 0;
    bar.current?.style.setProperty('transform', `scaleX(${reduced ? 1 : 0})`);
  }, [index, reduced]);

  useEffect(() => {
    if (!running) return;

    let frame = 0;
    let last = performance.now();
    const step = (now: number) => {
      elapsed.current += now - last;
      last = now;

      if (elapsed.current >= SLIDE_MS) {
        elapsed.current = 0;
        setIndex((i) => (i + 1) % SLIDES.length);
      }
      bar.current?.style.setProperty('transform', `scaleX(${elapsed.current / SLIDE_MS})`);
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);

    return () => cancelAnimationFrame(frame);
  }, [running]);

  const onBlur = (e: FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false);
  };

  return (
    <aside
      aria-roledescription="carrossel"
      aria-label="Destaques do Live Gospel"
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={onBlur}
      className="relative flex min-h-dvh flex-col justify-center gap-9 overflow-hidden bg-sidebar p-14 text-sidebar-ink scheme-light"
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(var(--sidebar-card-line)_1px,transparent_1px)] bg-size-[22px_22px]"
      />
      {SLIDES.map((slide, i) => (
        <div
          key={slide.key}
          aria-hidden
          className={cn(
            'absolute -top-45 -right-40 size-130 rounded-pill transition-opacity duration-700',
            slide.glow,
            i !== index && 'opacity-0'
          )}
        />
      ))}

      <div className="relative grid" aria-live={running ? 'off' : 'polite'}>
        {SLIDES.map(({ key, label, Slide }, i) => {
          const active = i === index;

          return (
            <div
              key={key}
              role="group"
              aria-roledescription="destaque"
              aria-label={`${i + 1} de ${SLIDES.length}: ${label}`}
              aria-hidden={!active || undefined}
              inert={!active}
              className={cn(
                'col-start-1 row-start-1 flex flex-col gap-9 transition-[opacity,translate] duration-500 ease-out',
                active ? 'opacity-100' : 'pointer-events-none translate-y-3 opacity-0'
              )}
            >
              <Slide active={active} />
            </div>
          );
        })}
      </div>

      <div className="relative flex max-w-115 items-end gap-3">
        <div role="group" aria-label="Escolher destaque" className="flex flex-1 gap-3">
          {SLIDES.map(({ key, label }, i) => {
            const active = i === index;

            return (
              <button
                key={key}
                type="button"
                aria-current={active || undefined}
                onClick={() => setIndex(i)}
                className="group flex flex-1 cursor-pointer flex-col gap-2 border-0 bg-transparent p-0 pt-2 text-left"
              >
                <span className="block h-0.5 overflow-hidden rounded-pill bg-sidebar-card-line">
                  {active && (
                    <span
                      ref={bar}
                      className="block h-full origin-left bg-sidebar-accent"
                      style={{ transform: `scaleX(${reduced ? 1 : 0})` }}
                    />
                  )}
                </span>
                <span
                  className={cn(
                    'text-meta font-semibold transition-colors',
                    active ? 'text-white' : 'text-sidebar-muted group-hover:text-sidebar-ink'
                  )}
                >
                  {label}
                </span>
              </button>
            );
          })}
        </div>
        {!reduced && (
          <button
            type="button"
            onClick={() => setStopped((s) => !s)}
            aria-label={stopped ? 'Retomar destaques' : 'Pausar destaques'}
            className="grid size-8 shrink-0 cursor-pointer place-items-center rounded-pill border border-sidebar-card-line bg-sidebar-card text-sidebar-ink transition-colors hover:bg-sidebar-hover hover:text-white"
          >
            {stopped ? <Play className="size-3.5 stroke-2" /> : <Pause className="size-3.5 stroke-2" />}
          </button>
        )}
      </div>
    </aside>
  );
}
